import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';

import { app, media } from 'context';
import { IconButton, Img, Layout } from 'ds';

import AIAssistantTypingMessage from './AIAssistantTypingMessage';
import AIAssistantUserMessage from './AIAssistantUserMessage';
import AnimateMessage from './AnimateMessage';
import AIAssistantConsumablesMessageCopy, {
  buildConsumablesSelectionIntroHtml
} from './AIAssistantWidgets/AIAssistantConsumablesMessageCopy';
import AIAssistantConsumablesProductPicker, {
  type ConsumablesPickerConfirmDetails
} from './AIAssistantWidgets/AIAssistantConsumablesProductPicker';
import AIAssistantViewSupportTicket from './AIAssistantWidgets/AIAssistantViewSupportTicket';
import type { AIAssistantMessage as AIAssistantMessageType } from './types';
import { PANTRY_SUBSCRIPTION_PATH } from 'routes';

interface Props {
  message: AIAssistantMessageType;
  handleAddFeedback: (messageId: number, liked: boolean) => void;
  conversationId: number;
  createdSincePageLoad: boolean;
}

const AIAssistantMessage: React.FC<Props> = ({ message, handleAddFeedback, conversationId, createdSincePageLoad }) => {
  const [messageIdHovered, setMessageIdHovered] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [typingComplete, setTypingComplete] = useState(!createdSincePageLoad);
  const [consumablesConfirmDetails, setConsumablesConfirmDetails] = useState<ConsumablesPickerConfirmDetails | null>(
    null
  );

  useEffect(() => {
    setTypingComplete(!createdSincePageLoad);
  }, [message.id, createdSincePageLoad]);

  useEffect(() => {
    setConsumablesConfirmDetails(null);
  }, [message.id]);
  const { isMobile } = useContext(media);
  const { appPaddingX } = useContext(app);

  if (message.role === 'user') {
    return (
      <Layout marginLeft={24} marginRight={isMobile ? -appPaddingX + 24 : undefined} justify="flex-end" flex>
        <AIAssistantUserMessage id={`user-message-${message.id}`}>
          <div dangerouslySetInnerHTML={{ __html: message.content }} />
        </AIAssistantUserMessage>
      </Layout>
    );
  } else if (message.role === 'assistant') {
    const consumablesPicker = message.metadata?.consumables_product_picker;
    const consumablesOrderEditUrl = consumablesPicker?.order_edit_url ?? null;
    const assistantMessageContent = (
      <div
        onMouseOver={() => setMessageIdHovered(message.id)}
        onMouseLeave={() => setMessageIdHovered(null)}
        style={{ width: '100%', minWidth: 0 }}
      >
        <Layout marginTop={isMobile ? 20 : 16} flex direction="column" width="100%" minWidth={0}>
          <Layout align="flex-start" flexShrink={consumablesPicker ? 1 : 0} width="100%" minWidth={0}>
            {!isMobile && (
              <Layout width={40} height={40} minWidth={40} minHeight={40} flexShrink={0} key="ai-assistant-icon">
                <Img
                  srcKey={'new_version_Ivy_Icon_3d_2UmlF3Tey.png'}
                  alt="AI Assistant Avatar"
                  width={400}
                  height={400}
                  folder="ux"
                />
              </Layout>
            )}
            <Layout
              __className={classNames({
                AIAssistantMessageBubble: Boolean(consumablesPicker),
                'has-consumables-picker': Boolean(consumablesPicker),
                'has-consumables-picker--mobile': Boolean(consumablesPicker) && isMobile
              })}
              color="white"
              paddingY={8}
              marginLeft={isMobile ? undefined : 12}
              borderTopRightRadius={16}
              borderBottomRightRadius={16}
              borderTopLeftRadius={16}
              minHeight={40}
              {...(consumablesPicker
                ? { flexGrow: 1, width: isMobile ? '100%' : 'calc(100% - 52px)', minWidth: 0 }
                : {})}
            >
              {consumablesPicker ? (
                consumablesConfirmDetails !== null ? (
                  <AIAssistantConsumablesMessageCopy
                    phase="confirmed"
                    applyMode={consumablesConfirmDetails.applyMode}
                    subscriptionPath={PANTRY_SUBSCRIPTION_PATH}
                    nextOrderLockPhrase={consumablesConfirmDetails.nextOrderLockPhrase}
                    nextOrderEditPath={consumablesConfirmDetails.nextOrderEditPath}
                    fallbackOrderEditUrl={consumablesOrderEditUrl}
                  />
                ) : createdSincePageLoad && !typingComplete ? (
                  <AIAssistantTypingMessage
                    message={{
                      ...message,
                      content: buildConsumablesSelectionIntroHtml(consumablesOrderEditUrl)
                    }}
                    onComplete={() => {
                      setTypingComplete(true);
                      setShowFeedback(true);
                    }}
                  />
                ) : (
                  <AIAssistantConsumablesMessageCopy phase="selection" orderEditUrl={consumablesOrderEditUrl} />
                )
              ) : createdSincePageLoad ? (
                <AIAssistantTypingMessage
                  message={message}
                  onComplete={() => {
                    setTypingComplete(true);
                    setShowFeedback(true);
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{ __html: message.content }}
                  style={{
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0.32px'
                  }}
                />
              )}
              {consumablesPicker && typingComplete ? (
                <AIAssistantConsumablesProductPicker
                  key={`consumables-picker-${message.id}`}
                  payload={consumablesPicker}
                  conversationId={conversationId}
                  messageId={message.id}
                  onConfirm={setConsumablesConfirmDetails}
                  onRestoreConfirmDetails={setConsumablesConfirmDetails}
                />
              ) : null}
            </Layout>
          </Layout>
          {!message.has_given_feedback && (
            <Layout
              flex
              direction="row"
              marginLeft={isMobile ? undefined : 64}
              gap={2}
              {...(isMobile
                ? { visibility: showFeedback ? 'visible' : 'hidden' }
                : { visibility: messageIdHovered === message.id && showFeedback ? 'visible' : 'hidden' })}
            >
              <IconButton
                name="thumbsUp"
                iconColor="gray-400"
                hoverIconColor="gray-700"
                type="noBackground"
                size="sm"
                stroke={2}
                onClick={() => handleAddFeedback(message.id, true)}
              />
              <IconButton
                name="thumbsDown"
                iconColor="gray-400"
                hoverIconColor="gray-700"
                type="noBackground"
                size="sm"
                stroke={2}
                onClick={() => handleAddFeedback(message.id, false)}
              />
            </Layout>
          )}
        </Layout>
      </div>
    );

    return <AnimateMessage>{assistantMessageContent}</AnimateMessage>;
  } else if (message.role === 'metadata') {
    if (message.metadata.type === 'support_ticket') {
      return (
        <Layout marginLeft={24} marginTop={16}>
          <AIAssistantViewSupportTicket
            action={message.metadata.action}
            supportTicket={message.metadata.support_ticket}
          />
        </Layout>
      );
    }
    return null;
  }

  return null;
};

export default AIAssistantMessage;
